// Shared types, default palette and image catalog for the "Spotlight Webinar
// Invite" template. Kept in a single non-React module so the editor and the
// HTML generator never circular-import each other.
//
// The LAYOUT is fixed on purpose — it mirrors the vendor webinar invitation the
// CTO picked as the house style (2026-09-23): logo → full-width banner →
// "joint webinar | date" eyebrow → two-part title → Register Now → "Hi <first
// name>," → intro → "During the webinar, we'll cover:" bullets → bold closing
// line → "When?" → Register Now → "Meet Our Speakers" (round headshots) →
// footer. What an operator may change is the CONTENT, the IMAGES and the
// COLOURS — never the order of the blocks.

// The XOP palette the template starts from. Hard-coded hex (not
// `theme.palette.*`) on purpose: this string HTML is emailed and rendered by
// mail clients that have no access to the MUI theme. The navy + lime match
// `apps/web/src/styles/theme.ts` (`brand` / `brandLime`).
export interface SpotlightColors {
  // Eyebrow line, the title's second line, section headings ("When?", "Meet
  // Our Speakers") and inline body links.
  accent: string
  // The two Register Now buttons.
  button: string
  // Text on the buttons.
  buttonText: string
  // The title's first line and speaker names.
  heading: string
  // Body copy.
  text: string
  // The page behind the white card.
  pageBackground: string
  // The white card the email sits on.
  cardBackground: string
}

export const DEFAULT_SPOTLIGHT_COLORS: SpotlightColors = {
  accent: "#2a3e6a",
  button: "#acce3e",
  buttonText: "#18213b",
  heading: "#18213b",
  text: "#3f4859",
  pageBackground: "#eef0f4",
  cardBackground: "#ffffff",
}

// One-click palettes so an operator can re-skin the invite without picking
// seven colours by hand. The first is the default.
export const SPOTLIGHT_PALETTES: ReadonlyArray<{ id: string; name: string; colors: SpotlightColors }> = [
  { id: "xop", name: "XOP navy + lime", colors: DEFAULT_SPOTLIGHT_COLORS },
  {
    id: "ocean",
    name: "Ocean blue",
    colors: { accent: "#0b6bcb", button: "#0b6bcb", buttonText: "#ffffff", heading: "#10233f", text: "#3f4859", pageBackground: "#edf3fa", cardBackground: "#ffffff" },
  },
  {
    id: "emergency",
    name: "Emergency red",
    colors: { accent: "#c8102e", button: "#c8102e", buttonText: "#ffffff", heading: "#1f1f1f", text: "#444444", pageBackground: "#f3f3f3", cardBackground: "#ffffff" },
  },
  {
    id: "desert",
    name: "Desert gold",
    colors: { accent: "#9a6b12", button: "#1d5c4d", buttonText: "#ffffff", heading: "#1d2a26", text: "#44504c", pageBackground: "#f5f1e8", cardBackground: "#ffffff" },
  },
]

export interface SpotlightSpeaker {
  id: string
  name: string
  // One or two lines ("CEO and Lead Analyst,\nAragon Research"): a newline
  // becomes a line break in the email.
  title: string
  // Headshot (a `/stock-images/*` path or an `/api/v1/image/*` upload).
  // Rendered as a circle. Blank → an initials circle in the accent colour.
  photoUrl: string
}

// One flat content bag — the template is a single fixed layout.
export interface SpotlightContent {
  // Inbox preview text (hidden in the body).
  preheader: string
  // Logo in the header. Blank → the regular XOP logo (XOP_EMAIL_LOGO_URL).
  logoUrl: string
  // Full-width banner under the logo.
  bannerUrl: string
  // "XOP Networks & Partner Joint Webinar | September 30, 2026".
  eyebrow: string
  // Title line 1 (bold) and line 2 (italic, accent colour).
  title: string
  subtitle: string
  // Label + destination shared by BOTH Register Now buttons.
  buttonLabel: string
  registrationLink: string
  // "Hi {{first_name}}," — crm-api fills the token per recipient.
  greeting: string
  // Rich-text intro (TipTap HTML) — the two paragraphs under the greeting.
  intro: string
  coverHeading: string
  // One bullet per entry.
  coverItems: string[]
  // The bold closing line under the bullets. Hidden when blank.
  closingLine: string
  whenHeading: string
  // "September 30, 2026 | 11:00 AM AST (8:00 AM GMT)".
  when: string
  speakersHeading: string
  speakers: SpotlightSpeaker[]
  colors: SpotlightColors
}

export interface SpotlightSection {
  id: string
  type: "spotlight"
  content: SpotlightContent
}

export const SPOTLIGHT_MAX_SPEAKERS = 4

// The regular horizontal XOP logo (ball + "XOP / Networks"), rendered from
// apps/web/public/xop-logo.svg to a trimmed 924×330 PNG — email clients don't
// show SVG. Absolute on purpose: it is served by THIS app's host
// (apps/web/public/images/xop-logo-email.png), not the support host
// `toAbsoluteUrl` anchors relative paths to. The tenant `logo_url` is the old
// stacked mark and is deliberately NOT the default.
export const XOP_EMAIL_LOGO_URL = "https://crm.xopnetworks.com/images/xop-logo-email.png"

// The default banner: a BRIGHT, friendly first-responder collage in the style
// marketing supplied (2026-09-24) — daylight and blue sky, an airport tower +
// jet, a fire truck, a calm police officer on a headset, paramedics with a
// patient, a firefighter, a soldier on a radio, a distant refinery flare, joined
// by light network lines. NOT dark or smoky (a dark montage was rejected as "too
// horrific"). A SHORT 3:1 strip, 1200×400 (a 1200×672 one was rejected as far
// too tall). People generic: no flags, insignia or text. AI-generated (OpenAI
// gpt-image-1). B is an alternative render. Absolute URLs: they live in THIS
// app's `public/images/`.
export const SOC_BANNER_URL = "https://crm.xopnetworks.com/images/emergency-banner-a.jpg"
export const SOC_BANNER_ALT_URL = "https://crm.xopnetworks.com/images/emergency-banner-b.jpg"

// Banner options — the emergency collages first (A = default),
// then the shared stock catalog shots that suit a webinar banner.
export const spotlightBannerOptions = [
  { id: -1, url: SOC_BANNER_URL, name: "First Responders A" },
  { id: 0, url: SOC_BANNER_ALT_URL, name: "First Responders B" },
  { id: 1, url: "/stock-images/tech-conference.jpg", name: "Tech Conference" },
  { id: 2, url: "/stock-images/control-room.jpg", name: "Control Room" },
  { id: 3, url: "/stock-images/network-infrastructure.jpg", name: "Network Infrastructure" },
  { id: 4, url: "/stock-images/emergency-vehicles.jpg", name: "Emergency Vehicles" },
  { id: 5, url: "/stock-images/communication-tower.jpg", name: "Communication Tower" },
  { id: 6, url: "/stock-images/virtual-meeting.jpg", name: "Virtual Meeting" },
  { id: 7, url: "/stock-images/digital-presentation.jpg", name: "Digital Presentation" },
  { id: 8, url: "/stock-images/server-room.jpg", name: "Server Room" },
]

// Re-exported from the single source of truth (`crm/lib/public-url.ts`).
//   * toAbsoluteUrl     → the SENT email (always the public support host).
//   * toPreviewImageUrl → in-EDITOR <img> previews.
export { toAbsoluteUrl, toPreviewImageUrl } from "@crm-src/lib/public-url"
